import { Constants } from "./constants";

/**
 * AKC Herding armband numbering ranges.
 *
 * Per club convention (see data/prd/AKC_HERDING_ARMBAND_GENERATION_PRD.md):
 * each stock type + level combination gets its own starting number so armbands
 * are immediately recognizable (e.g. "started ducks" always begins in the 100s).
 *
 * Adjust these numbers to change the ranges - no other code changes required.
 */
export const HERDING_ARMBAND_RANGES: Record<string, Record<string, number>> = {
  [Constants.dog_class_section.ducks]: {
    [Constants.dog_class_element_level.started]: 100,
    [Constants.dog_class_element_level.intermediate]: 200,
    [Constants.dog_class_element_level.advance]: 300,
  },
  [Constants.dog_class_section.sheep]: {
    [Constants.dog_class_element_level.started]: 400,
    [Constants.dog_class_element_level.intermediate]: 500,
    [Constants.dog_class_element_level.advance]: 600,
  },
  [Constants.dog_class_section.cattle]: {
    [Constants.dog_class_element_level.started]: 700,
    [Constants.dog_class_element_level.intermediate]: 800,
    [Constants.dog_class_element_level.advance]: 900,
  },
};

/**
 * Pre-Trial (Herding) / Instinct Test (Herding) always use plain two-digit
 * numbers (10-99), no course letter, and are not subdivided by stock type.
 */
export const HERDING_PRE_TRIAL_INSTINCT_TEST_ARMBAND_START = 10;

/**
 * Suffix appended to the armband of a dog still on the waitlist for an AKC
 * Herding class ("Alternate") - e.g. "A103-ALT". See
 * data/prd/AKC_HERDING_ALTERNATES_PRD.md.
 */
export const HERDING_ALTERNATE_ARMBAND_SUFFIX = "-ALT";

/**
 * Returns the starting armband number for a given AKC Herding stock type + level,
 * or undefined if no range is configured for that combination.
 */
export function getHerdingArmbandRangeStart(
  section: string | undefined,
  level: string | undefined
): number | undefined {
  if (!section || !level) return undefined;
  return HERDING_ARMBAND_RANGES[section]?.[level];
}

/**
 * Returns the single-letter course prefix for an AKC Herding `show_element`
 * (e.g. "Course A" -> "A"), or undefined if it doesn't match that pattern
 * (e.g. Pre-Trial/Instinct Test, which use no letter).
 */
export function getHerdingCourseLetter(showElement: string | undefined): string | undefined {
  if (!showElement) return undefined;
  const match = showElement.match(/^Course\s+([A-Za-z])$/);
  return match ? match[1].toUpperCase() : undefined;
}
